import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { useClinic } from "@/contexts/ClinicContext";
import { Dentist, fetchDentists} from "@/services/dentist-service";
import {ConsultingRoom, fetchConsultingRooms} from "@/services/consulting-room-service";
import {Patient, fetchPatients} from "@/services/patient-service";
import {DentalSpecialty, fetchDentalSpecialties} from "@/services/dental-specialty-service";
import {Insurance, fetchInsurances} from "@/services/insurance-service";
import {createFitInAppointment} from "@/services/appointment-service";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

const TIME_SLOTS = Array.from({ length: 22 }, (_, i) => {
  const hour = Math.floor(7.5 + i * 0.5);
  const minute = (7.5 + i * 0.5) % 1 === 0 ? "00" : "30";
  return `${hour.toString().padStart(2, "0")}:${minute}`;
}).filter(time => {
  const [hour] = time.split(":").map(Number);
  return hour < 18 || (hour === 18 && time === "18:00");
});

interface FitInAppointmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  initialClinicId?: string;
  initialDate?: string;
}

export const FitInAppointmentDialog = ({
  open, onOpenChange, onSuccess, initialClinicId, initialDate,
}: FitInAppointmentDialogProps) => {
  const { toast } = useToast();
  const { clinics } = useClinic();
  const [loading, setLoading] = useState(false);
  const [consultingRooms, setConsultingRooms] = useState<ConsultingRoom[]>([]);
  const [dentists, setDentists] = useState<Dentist[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [specialties, setSpecialties] = useState<DentalSpecialty[]>([]);
  const [insurances, setInsurances] = useState<Insurance[]>([]);

  const [clinicId, setClinicId] = useState(initialClinicId || "");
  const [consultingRoomId, setConsultingRoomId] = useState("");
  const [dentistId, setDentistId] = useState("");
  const [patientId, setPatientId] = useState("");
  const [specialtyId, setSpecialtyId] = useState("");
  const [insuranceId, setInsuranceId] = useState("");
  const [appointmentDate, setAppointmentDate] = useState(initialDate || "");
  const [appointmentTime, setAppointmentTime] = useState("");
  const [cardNumber, setCardNumber] = useState("");

  const [patientSearchOpen, setPatientSearchOpen] = useState(false);
  const [patientSearch, setPatientSearch] = useState("");

  useEffect(() => { if (open) loadInitialData(); }, [open]);
  useEffect(() => { if (clinicId) loadConsultingRooms(clinicId); }, [clinicId]);

  const loadInitialData = async () => {
    try {
      const [dentistsData, specialtiesData, insurancesData] = await Promise.all([
        fetchDentists(false, false, "", 1, 100),
        fetchDentalSpecialties(), fetchInsurances(),
      ]);
      setDentists(dentistsData);
      setSpecialties(specialtiesData); setInsurances(insurancesData);
    } catch (error) {
      toast({ title: "Erro ao carregar dados", description: error instanceof Error ? error.message : "Tente novamente", variant: "destructive" });
    }
  };

  const loadConsultingRooms = async (clinic: string) => {
    try {
      const rooms = await fetchConsultingRooms(clinic);
      setConsultingRooms(rooms);
    } catch (error) {
      toast({ title: "Erro ao carregar consultórios", description: error instanceof Error ? error.message : "Tente novamente", variant: "destructive" });
    }
  };

  const loadPatients = async (search: string) => {
    if (search.length < 2) return;
    try {
      const patientsData = await fetchPatients(false, false, search, 1, 50);
      setPatients(patientsData);
    } catch (error) {
      console.error("Erro ao buscar pacientes:", error);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => { if (patientSearch) loadPatients(patientSearch); }, 300);
    return () => clearTimeout(timer);
  }, [patientSearch]);

  const handleSubmit = async () => {
    if (!clinicId || !consultingRoomId || !dentistId || !patientId ||
        !specialtyId || !insuranceId || !appointmentDate || !appointmentTime) {
      toast({ title: "Campos obrigatórios", description: "Preencha todos os campos obrigatórios", variant: "destructive" });
      return;
    }
    try {
      setLoading(true);
      await createFitInAppointment({
        clinic_id: clinicId, consulting_room_id: consultingRoomId,
        appointment_date: appointmentDate, appointment_time: appointmentTime,
        dentist_id: dentistId, dental_specialty_id: specialtyId,
        patient_id: patientId, dental_insurance_id: insuranceId,
        card_number: cardNumber, import_key: "",
      });
      toast({ title: "Encaixe criado", description: "O encaixe foi registrado com sucesso" });
      onSuccess();
      onOpenChange(false);
      resetForm();
    } catch (error) {
      toast({ title: "Erro ao criar encaixe", description: error instanceof Error ? error.message : "Tente novamente", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setConsultingRoomId(""); setDentistId(""); setPatientId(""); setSpecialtyId("");
    setInsuranceId(""); setAppointmentTime(""); setCardNumber(""); setPatientSearch("");
  };

  const selectedPatient = patients.find(p => p.id === patientId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="border-b pb-2">
          <DialogTitle className="text-sm font-semibold uppercase tracking-wide">Cadastrar Encaixe</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="space-y-1">
            <Label className="filter-label" htmlFor="clinic">Clínica *</Label>
            <Select value={clinicId} onValueChange={setClinicId}>
              <SelectTrigger id="clinic" className="filter-input"><SelectValue placeholder="Selecione a clínica" /></SelectTrigger>
              <SelectContent>{clinics.map((c) => <SelectItem key={c.id} value={c.id}>{c.trade_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="filter-label" htmlFor="room">Consultório *</Label>
            <Select value={consultingRoomId} onValueChange={setConsultingRoomId} disabled={!clinicId}>
              <SelectTrigger id="room" className="filter-input"><SelectValue placeholder="Selecione o consultório" /></SelectTrigger>
              <SelectContent>{consultingRooms.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="filter-label" htmlFor="dentist">Dentista *</Label>
            <Select value={dentistId} onValueChange={setDentistId}>
              <SelectTrigger id="dentist" className="filter-input"><SelectValue placeholder="Selecione o dentista" /></SelectTrigger>
              <SelectContent>{dentists.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="filter-label" htmlFor="specialty">Especialidade *</Label>
            <Select value={specialtyId} onValueChange={setSpecialtyId}>
              <SelectTrigger id="specialty" className="filter-input"><SelectValue placeholder="Selecione a especialidade" /></SelectTrigger>
              <SelectContent>{specialties.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="space-y-1 col-span-2">
            <Label className="filter-label">Paciente *</Label>
            <Popover open={patientSearchOpen} onOpenChange={setPatientSearchOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" aria-expanded={patientSearchOpen} className="w-full justify-between h-7 text-xs">
                  {selectedPatient ? selectedPatient.name : "Digite para buscar paciente..."}
                  <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput placeholder="Digite o nome do paciente..." value={patientSearch} onValueChange={setPatientSearch} />
                  <CommandList>
                    <CommandEmpty>
                      {patientSearch.length < 2 ? "Digite pelo menos 2 caracteres para buscar" : "Nenhum paciente encontrado"}
                    </CommandEmpty>
                    <CommandGroup>
                      {patients.map((patient) => (
                        <CommandItem key={patient.id} value={patient.id} onSelect={() => { setPatientId(patient.id); setPatientSearchOpen(false); }}>
                          <Check className={cn("mr-2 h-3 w-3", patientId === patient.id ? "opacity-100" : "opacity-0")} />
                          <div className="flex flex-col">
                            <span className="text-xs">{patient.name}</span>
                            <span className="text-xs text-muted-foreground">{patient.cpf}</span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-1">
            <Label className="filter-label" htmlFor="insurance">Convênio *</Label>
            <Select value={insuranceId} onValueChange={setInsuranceId}>
              <SelectTrigger id="insurance" className="filter-input"><SelectValue placeholder="Selecione o convênio" /></SelectTrigger>
              <SelectContent>{insurances.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="filter-label" htmlFor="cardNumber">Número da Carteirinha</Label>
            <Input className="filter-input" id="cardNumber" value={cardNumber} onChange={(e) => setCardNumber(e.target.value)} placeholder="Digite o número" />
          </div>

          <div className="space-y-1">
            <Label className="filter-label" htmlFor="date">Data *</Label>
            <Input className="filter-input" id="date" type="date" value={appointmentDate} onChange={(e) => setAppointmentDate(e.target.value)} />
          </div>

          <div className="space-y-1">
            <Label className="filter-label" htmlFor="time">Horário *</Label>
            <Select value={appointmentTime} onValueChange={setAppointmentTime}>
              <SelectTrigger id="time" className="filter-input"><SelectValue placeholder="Selecione o horário" /></SelectTrigger>
              <SelectContent>{TIME_SLOTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t">
          <Button variant="outline" size="sm" className="btn-header" onClick={() => onOpenChange(false)} disabled={loading}>Cancelar</Button>
          <Button size="sm" className="btn-header" onClick={handleSubmit} disabled={loading}>
            {loading ? "Salvando..." : "Cadastrar Encaixe"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
